<template>
  <div v-if="data.length < 2" class="title-block">
    Dans quelle campagne souhaitez vous déplacer
    <span v-if="data.length > 1">ces {{ data.length }} talents</span>
    <span class="primary-color bold" v-else v-html="candidateWording" />
    ?
  </div>
  <p v-if="data.length > 1" class="under-text">
    Les talents sont actuellement inscrit dans la campagne
    <span class="bold">{{ campaignStore.currentCampaign.name }}</span
    >. Si vous les déplacez, ils seront supprimés de cette campagne et ajoutés
    dans la campagne de destination.
  </p>
  <p v-else class="under-text">
    Le talent est actuellement inscrit dans la campagne
    <span class="bold">{{ campaignStore.currentCampaign.name }}</span
    >. Si vous le déplacez, il sera supprimé de cette campagne et ajouté dans la
    campagne de destination.
  </p>
  <div class="input-wrapper">
    <label v-text="'Choisir l’espace de destination'" />
    <v-select
      id="campaign-space"
      placeholder="Sélectionnez l’espace dans lequel vous souhaitez déplacer le talent"
      name="campaign-space"
      label="name"
      v-model="state.space"
      :clearable="false"
      :options="spaceStore.spaces"
    >
      <template v-slot:no-options="{ search, searching }">
        <div style="padding: 8px 0">
          Pas de résultat {{ searching ? ` pour ${search}` : "" }}.
        </div>
      </template>
    </v-select>
  </div>
  <div class="input-wrapper">
    <label v-text="'Choisir la campagne de destination'" />
    <v-select
      id="campaign-space"
      placeholder="Sélectionnez la campagne dans lequel vous souhaitez déplacer le talent"
      name="campaign-space"
      label="name"
      v-model="state.campaign"
      :disabled="!state.space"
      :clearable="false"
      :options="
        campaignStore.campaigns.filter(
          (obj) =>
            obj.workspace_id === state.space?.workspace_id &&
            obj.campaign_id !== campaignStore.currentCampaign.campaign_id
        )
      "
    >
      <template v-slot:no-options="{ search, searching }">
        <div style="padding: 8px 0">
          Pas de résultat {{ searching ? ` pour ${search}` : "" }}.
        </div>
      </template>
    </v-select>
  </div>
  <div class="btn-container">
    <Button
      class="secondary outline"
      label="Annuler l’opération"
      @click="emits('close')"
    />
    <Button
      class="primary"
      :loading="state.btnIsLoading"
      :disabled="!state.campaign"
      :label="`Déplacer ${data.length > 1 ? 'les talents' : 'le talent'}`"
      @click="moveParticipants"
    />
  </div>
</template>

<script setup>
import { computed, reactive, watch, inject } from "vue";
import { useCampaignStore } from "@/stores/campaign.js";
import { useSpaceStore } from "@/stores/space.js";
import { useRoute } from "vue-router";
import { useToast } from "vue-toastification";
import { getError } from "@/composables/error.js";
import { useParticipantStore } from "@/stores/participant.js";
import ToastContent from "@/components/global/ToastContent.vue";

const emitter = inject("emitter");

const toast = useToast();

const route = useRoute();

const emits = defineEmits(["close", "confirm"]);

// Init stores
const participantStore = useParticipantStore();
const campaignStore = useCampaignStore();
const spaceStore = useSpaceStore();

const props = defineProps({
  data: {
    type: Array,
    required: true,
  },
});

const state = reactive({
  space: null,
  campaign: null,
  btnIsLoading: false,
});

const candidateWording = computed(() => {
  let candidate;

  if (props.data[0].candidate_id) {
    candidate = `${props.data[0].candidate.first_name} ${props.data[0].candidate.last_name}`;
  } else if (
    props.data[0].pending_first_name &&
    props.data[0].pending_last_name
  ) {
    candidate = `${props.data[0].pending_first_name} ${props.data[0].pending_last_name}`;
  } else {
    candidate = props.data[0].pending_email;
  }

  return candidate;
});

async function moveParticipants() {
  state.btnIsLoading = true;

  const resp = await participantStore.moveParticipants(
    route.params.companyId,
    route.params.spaceId,
    route.params.campaignId,
    props.data.map((elt) => elt.participant_id),
    state.campaign.campaign_id
  );

  if (resp.code) {
    toast.error(getError(resp.key));
  } else {
    const url = `/companies/${state.campaign.company_id}/spaces/${state.campaign.workspace_id}/campaigns/${state.campaign.campaign_id}`;

    toast.success({
      component: ToastContent,
      props: {
        text:
          props.data.length > 1
            ? `Les talents ont bien été déplacés vers la campagne <a href='${url}' class='bold'>${state.campaign.name}</a>.`
            : `Le talent a bien été déplacé vers la campagne <a href='${url}' class='bold'>${state.campaign.name}</a>.`,
      },
      closeOnClick: false,
    });

    if (props.data.length > 1) emitter.emit("confirmBulk");
  }

  emits("close");

  state.btnIsLoading = true;
}

watch(
  () => state.space,
  async () => {
    state.campaign = null;

    if (!state.space.fetchedCampaigns) {
      await campaignStore.getCampaigns(
        route.params.companyId,
        state.space.workspace_id
      );
    }
  }
);
</script>
